import { create } from "zustand";

import type { Route } from "@user/routes";
import { useSettingsStore } from "@user/store/settings_store";

interface Location {
  key: number,
  path: string,
  search: string,
};

interface TabItem {
  id: string;
  label: string;
  key: number;
  path: string;
  search: string;
  component?: any;
  isInit: boolean;
  isFavorite: boolean;
};

interface TabsStoreState {
  tabs: TabItem[];
  currTabId: string;
  init: () => Promise<boolean>;
  getTab: (id: string) => TabItem | undefined;
  getCurrTab: () => TabItem | undefined;
  addTab: (path: string, search: string) => TabItem;
  changeTab: (id?: string) => void;
  removeTab: (id: string) => void;
  setFavorite: (id: string) => void;
  unsetFavorite: (id: string) => void;
  toggleFavorite: (id?: string) => void;
  getLocation: (id: string) => Location;
}

let tabKey = 0;

function saveState(state: TabsStoreState) {
  useSettingsStore.getState().setLocalSetting("tabs", "state", {
    tabs: state.tabs,
    currTabId: state.currTabId
  });
}

async function saveFavorite(state: TabsStoreState) {
  const favorites: string[] = [];
  state.tabs.forEach(tab => {
    if (tab.isFavorite) {
      favorites.push(tab.id);
    }
  });
  useSettingsStore.getState().setSetting("tabs.favorites", {favorites}, 0);
}

export const DEFAULT_TAB_ID = "/";
export const useTabsStore = create<TabsStoreState>((set, get) => ({
  tabs: [],
  currTabId: "",
  init: async () => {
    const mainRoute = resolveRoute(DEFAULT_TAB_ID);
    const mainTab: TabItem = {
      id: mainRoute.id!,
      label: mainRoute.label || "NO LABEL",
      key: ++tabKey,
      path: mainRoute.path,
      search: "",
      component: mainRoute.component,
      isInit: false,
      isFavorite: false,
    };

    try {
      const savedState: TabsStoreState = useSettingsStore.getState().getLocalSetting("tabs", "state") || {"tabs":[], "currTabId":DEFAULT_TAB_ID};
       // savedState.currTabId = savedState.currTabId || DEFAULT_TAB_ID;
      savedState.currTabId = DEFAULT_TAB_ID; // 초기화시 메인페이지로 이동
      const savedTabs = savedState.tabs.filter(tab => tab.id != mainTab.id);

      const favorites: string[] = useSettingsStore.getState().getSetting("tabs.favorites")?.favorites || [];
      const newTabs: TabItem[] = [];
      favorites.forEach(id => {
        if (id == mainTab.id) return;
        let tab = savedTabs.find(tab => tab.id == id);
        if (tab) {
          tab.component = resolveRoute(tab.path).component;
          tab.isInit = tab.id == savedState.currTabId;
          tab.key = ++tabKey;
        } else {
          tab = get().addTab(id, "");
        }
        tab.isFavorite = true;
        newTabs.push(tab);
      });
      savedTabs.filter(tab => tab.id != mainTab.id && !favorites.find(id => id == tab.id)).forEach(tab => {
        tab.component = resolveRoute(tab.path).component;
        tab.isInit = tab.id == savedState.currTabId;
        tab.key = ++tabKey;
        tab.isFavorite = false;
        newTabs.push(tab);
      });
      mainTab.isInit = mainTab.id == savedState.currTabId;
      set({tabs: [mainTab, ...newTabs]});
      set({currTabId: savedState.currTabId});
      saveState(get());
      return true;
    } catch(e) {
      console.log(e);
      set({tabs: []});
      set({currTabId: ""});
      get().addTab(DEFAULT_TAB_ID, "");
      return false;
    }
  },
  getTab: (id: string): TabItem | undefined => {
    return get().tabs.find((tab: any) => tab.id == id)!;
  },
  getCurrTab: (): TabItem | undefined => {
    return get().getTab(get().currTabId);
  },
  addTab: (path: string, search: string) => {
    const route = resolveRoute(path);

    // 이미 존재하는 탭인 경우
    const existTab = get().tabs.find((tab: any) => tab.id == route.id);
    if (existTab) {
      existTab.key = ++tabKey;
      if (existTab.path !== path) {
        existTab.path = path;
        const route = resolveRoute(path);
        existTab.component = route.component;
      }
      existTab.search = search;
      existTab.isInit = true;
      set({tabs: get().tabs.map(tab => tab.id === existTab.id ? { ...existTab } : tab)});
      set({currTabId: existTab.id});
      saveState(get());
      return existTab;
    }

    // 신규 탭인 경우
    const newTab: TabItem = {
      id: route.id!,
      label: route.label || "NO LABEL",
      key: ++tabKey,
      path: route.path,
      search: search,
      component: route.component,
      isInit: true,
      isFavorite: false,
    };
    set({tabs: [...get().tabs, newTab]});
    set({currTabId: newTab.id});
    saveState(get());
    return newTab;
  },
  changeTab: (id: string = DEFAULT_TAB_ID) => {
    if (get().currTabId == id) return;

    const tab = get().getTab(id);
    if (!tab) return;

    tab.isInit = true;
    set({tabs: [...get().tabs]});
    set({currTabId: id});
    saveState(get());
  },
  removeTab: (id: string) => {
    const tabs = get().tabs;
    const newTabs = tabs.filter((tab: any) => tab.id !== id);
    if (newTabs.length == 0) {
      set({currTabId: ""});
    } else if (get().currTabId == id) {
      // 현재탭이 삭제되는 경우
      const currTabIdx = tabs.findIndex((tab: any) => tab.id == id);
      const newTabId = currTabIdx < newTabs.length ? newTabs[currTabIdx].id : newTabs[newTabs.length - 1].id;
      set({currTabId: newTabId});
      const newTab = get().getTab(newTabId)!;
      newTab.isInit = true;
    }
    set({tabs: newTabs});
    saveState(get());
  },
  setFavorite: (id: string) => {
    const tab = get().getTab(id)!;
    tab.isFavorite = true;
    set({tabs: [...get().tabs]});
    saveState(get());
    saveFavorite(get());
  },
  unsetFavorite: (id: string) => {
    const tab = get().getTab(id)!;
    tab.isFavorite = false;
    set({tabs: [...get().tabs]});
    saveState(get());
    saveFavorite(get());
  },
  toggleFavorite: (id?: string) => {
    const tab = get().getTab(id || get().currTabId)!;
    tab.isFavorite = !tab.isFavorite;
    set({tabs: [...get().tabs]});
    saveState(get());
    saveFavorite(get());
  },
  getLocation: (id: string): Location => {
    return get().getTab(id)!;
  }
}));

let resolveRoute: (path: string) => Route = () => {
  throw new Error("route resolver가 등록되지 않았습니다.");
};

export function registerRouteResolver(fn: (path: string) => Route) {
  resolveRoute = fn;
}
